import { MAX_PAGE_SIZE } from '@aysar/contracts';
import { useCallback, useMemo, useState } from 'react';

import { useDebouncedValue } from '@/shared/hooks/useDebouncedValue/useDebouncedValue';

import {
  type UseServerSelectQuery,
  type UseServerSelectResolve,
} from '../types/server-selection.types';
import { type SelectionOption } from '../types/selection-option.types';

/** The first page. Three doublings reach the api's hard ceiling. */
export const SERVER_SELECT_PAGE_SIZE = 20;

/** Fire the sentinel before it is visible, so the next rows are already in flight. */
export const SERVER_SELECT_PREFETCH_MARGIN = '96px';

export interface UseServerOptionsInput {
  /** Values that must resolve regardless of which page is loaded. */
  pinnedValues: readonly string[];
  useOptionsQuery: UseServerSelectQuery;
  useResolveIds?: UseServerSelectResolve;
}

export interface UseServerOptions {
  search: string;
  setSearch: (search: string) => void;
  /** Resolved-by-value items first, then the page — no duplicates. */
  options: SelectionOption[];
  /** value → option, so a chip names itself without scanning the list. */
  byValue: ReadonlyMap<string, SelectionOption>;
  isFetching: boolean;
  isResolving: boolean;
  isError: boolean;
  canLoadMore: boolean;
  loadMore: () => void;
  /** Undefined when the caller's query cannot be re-run. */
  retry: (() => void) | undefined;
}

const NO_RESOLVE: UseServerSelectResolve = () => ({ items: [], isFetching: false });

/**
 * The options behind every server-backed picker — single and multi alike.
 *
 * **Nothing accumulates across renders.** The obvious design — append each new
 * page to a `useState` list inside an effect — is a `set-state-in-effect`
 * violation (an error here), and it also has to hand-solve duplicates, ordering
 * after a mutation, and resetting when the search changes. Instead, "load more"
 * **widens the page**: the request asks for `20 → 40 → 80 → 100` rows in one go,
 * and the answer is always the complete, correctly ordered list. RTK Query
 * caches each width, so the widened fetch is one request and the previous rows
 * stay on screen while it flies.
 *
 * The step DOUBLES rather than adding a page, because a scroll sentinel fires
 * far more often than a button was ever clicked: a fast scroll through a
 * hundred rows would otherwise fetch 20+40+60+80+100 = 300 of them.
 *
 * **Saved values are resolved separately and pinned first.** A value stored last
 * month may sort to page 9 today, or be excluded by the current search
 * altogether; a picker that could not render its own value would show a blank
 * trigger and lose the value on the next save. The resolve request is clamped to
 * `MAX_PAGE_SIZE` for the same reason the page query is — the api REFUSES an
 * over-large page rather than clamping it (N7), so a form holding 150 saved
 * values would 422 every resolve instead of naming any of them.
 */
export function useServerOptions({
  pinnedValues,
  useOptionsQuery,
  useResolveIds = NO_RESOLVE,
}: UseServerOptionsInput): UseServerOptions {
  const [search, setSearch] = useState('');
  const [pageSize, setPageSize] = useState(SERVER_SELECT_PAGE_SIZE);
  const debouncedSearch = useDebouncedValue(search, 300);

  const query = useOptionsQuery({ search: debouncedSearch, page: 1, pageSize });

  const resolvable = useMemo(() => pinnedValues.slice(0, MAX_PAGE_SIZE), [pinnedValues]);
  const resolved = useResolveIds(resolvable);

  const options = useMemo(() => {
    const onPage = new Set(query.items.map((item) => item.value));
    const pinned = resolved.items.filter((item) => !onPage.has(item.value));

    return [...pinned, ...query.items];
  }, [query.items, resolved.items]);

  const byValue = useMemo(
    () => new Map(options.map((option) => [option.value, option])),
    [options],
  );

  const changeSearch = useCallback((next: string) => {
    setSearch(next);
    // A new search is a new list: keeping a widened page would fetch a hundred
    // rows for a query that has three. In the HANDLER, never an effect watching
    // `search` — that is a `set-state-in-effect` violation.
    setPageSize(SERVER_SELECT_PAGE_SIZE);
  }, []);

  const loadMore = useCallback(() => {
    setPageSize((current) => Math.min(current * 2, MAX_PAGE_SIZE));
  }, []);

  return {
    search,
    setSearch: changeSearch,
    options,
    byValue,
    isFetching: query.isFetching,
    isResolving: resolved.isFetching,
    isError: query.isError,
    canLoadMore: query.hasNext && pageSize < MAX_PAGE_SIZE,
    loadMore,
    retry: query.refetch,
  };
}
